import { useState, useCallback } from 'react';
import type { ClipboardPreview } from '@/types';
import { isMarkdown, isJson, isUrl, stripMarkupAndMarkdown } from '@/lib/textUtils';

export function useClipboard() {
  const [preview, setPreview] = useState<ClipboardPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readClipboard = useCallback(async (): Promise<ClipboardPreview | null> => {
    setLoading(true);
    setError(null);

    try {
      // Try Clipboard API first
      if (navigator.clipboard && navigator.clipboard.read) {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          // Check for image
          const imageType = item.types.find(t => t.startsWith('image/'));
          if (imageType) {
            const blob = await item.getType(imageType);
            const dataUrl = await blobToDataUrl(blob);
            const p: ClipboardPreview = { type: 'image', content: dataUrl, rawBlob: blob };
            setPreview(p);
            setLoading(false);
            return p;
          }

          // Check for text (Prioritize Markdown detection)
          if (item.types.includes('text/plain')) {
            const blob = await item.getType('text/plain');
            const text = await blob.text();
            if (text.trim()) {
              if (isJson(text)) {
                const p: ClipboardPreview = { type: 'json', content: JSON.stringify(JSON.parse(text), null, 2) };
                setPreview(p);
                setLoading(false);
                return p;
              }
              if (isMarkdown(text)) {
                const p: ClipboardPreview = { type: 'markdown', content: text };
                setPreview(p);
                setLoading(false);
                return p;
              }
              if (isUrl(text)) {
                const p: ClipboardPreview = { type: 'url', content: text.trim() };
                setPreview(p);
                setLoading(false);
                return p;
              }
              // If not markdown, json, or url, we keep the text but continue to check for HTML
              // unless there is ONLY plain text.
              if (!item.types.includes('text/html')) {
                const p: ClipboardPreview = { type: 'text', content: text };
                setPreview(p);
                setLoading(false);
                return p;
              }
            }
          }

          // Check for HTML
          if (item.types.includes('text/html')) {
            const blob = await item.getType('text/html');
            const html = await blob.text();
            // Even if it's HTML, check if the text content is JSON
            const plain = stripMarkupAndMarkdown(html);
            let p: ClipboardPreview;
            if (isJson(plain)) {
              p = { type: 'json', content: JSON.stringify(JSON.parse(plain), null, 2) };
            } else {
              p = { type: 'html', content: html };
            }
            setPreview(p);
            setLoading(false);
            return p;
          }
        }
        const empty: ClipboardPreview = { type: 'empty', content: '' };
        setPreview(empty);
        setLoading(false);
        return empty;
      } else if (navigator.clipboard && navigator.clipboard.readText) {
        // Fallback: text only
        const text = await navigator.clipboard.readText();
        if (text.trim()) {
          let type: ClipboardPreview["type"] = 'text';
          let content = text;
          if (isJson(text)) {
            type = 'json';
            content = JSON.stringify(JSON.parse(text), null, 2);
          } else if (isUrl(text)) {
            type = 'url';
            content = text.trim();
          } else if (isMarkdown(text)) {
            type = 'markdown';
          }
          const p: ClipboardPreview = { type, content };
          setPreview(p);
          setLoading(false);
          return p;
        } else {
          const empty: ClipboardPreview = { type: 'empty', content: '' };
          setPreview(empty);
          setLoading(false);
          return empty;
        }
      } else {
        setError('clipboard_not_supported');
        setPreview(null);
        setLoading(false);
        return null;
      }
    } catch (err) {
      // Permission denied or not focused
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('permission_denied');
      } else {
        setError('read_failed');
      }
      setPreview(null);
      setLoading(false);
      return null;
    }
  }, []);

  // Handle paste event (fallback for permission denied)
  const handlePaste = useCallback((e: ClipboardEvent | React.ClipboardEvent) => {
    const clipboardData = 'clipboardData' in e ? e.clipboardData : null;
    if (!clipboardData) return;

    // Check images
    const files = Array.from(clipboardData.files);
    const imageFile = files.find(f => f.type.startsWith('image/'));
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreview({ type: 'image', content: reader.result as string, rawBlob: imageFile });
      };
      reader.readAsDataURL(imageFile);
      return;
    }

    // Check text (Prioritize Markdown)
    const text = clipboardData.getData('text/plain');
    if (text.trim() && isMarkdown(text)) {
      setPreview({ type: 'markdown', content: text });
      return;
    }

    // Check HTML
    const html = clipboardData.getData('text/html');
    if (html) {
      const plain = stripMarkupAndMarkdown(html);
      if (isJson(plain)) {
        setPreview({ type: 'json', content: JSON.stringify(JSON.parse(plain), null, 2) });
      } else {
        setPreview({ type: 'html', content: html });
      }
      return;
    }

    // Fallback text
    if (text.trim()) {
      if (isJson(text)) {
        setPreview({ type: 'json', content: JSON.stringify(JSON.parse(text), null, 2) });
      } else if (isUrl(text)) {
        setPreview({ type: 'url', content: text.trim() });
      } else {
        setPreview({ type: 'text', content: text });
      }
    } else {
      setPreview({ type: 'empty', content: '' });
    }
  }, []);

  // Copy data to clipboard
  const copyToClipboard = useCallback(async (content: string, type: string = 'text'): Promise<boolean> => {
    try {
      // Image support
      if (type === 'image') {
        const res = await fetch(content);
        const blob = await res.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob }),
        ]);
        return true;
      }

      // HTML support (can also include plain text fallback in the same item)
      if (type === 'html') {
        const htmlBlob = new Blob([content], { type: 'text/html' });
        const textBlob = new Blob([stripMarkupAndMarkdown(content)], { type: 'text/plain' });
        await navigator.clipboard.write([
          new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob }),
        ]);
        return true;
      }

      // For all other types (text, markdown, json, url, etc.), use writeText
      await navigator.clipboard.writeText(content);
      return true;
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      return false;
    }
  }, []);

  const clearPreview = useCallback(() => {
    setPreview(null);
    setError(null);
  }, []);

  const clearClipboard = useCallback(async () => {
    setPreview(null);
    setError(null);
    try {
      await navigator.clipboard.writeText('');
    } catch (err) {
      console.error('Failed to clear OS clipboard:', err);
    }
  }, []);

  // Listen for external preview set events
  const setPreviewExternal = useCallback((p: ClipboardPreview) => {
    setPreview(p);
    setError(null);
  }, []);

  return { preview, loading, error, readClipboard, handlePaste, copyToClipboard, clearPreview, clearClipboard, setPreviewExternal };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
